import IntlMessages from '@app/components/utility/intlMessages';
import options from '@app/containers/NavMain/mainOptions';
import { Popover, Tooltip } from 'antd';
import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { LeftSidebarWrapper } from './LeftSidebar.style';
import { NavigationIcon } from './navigationIcons';
import { getNavUrl, isNavActive, isNavParentActive } from './navUtils';

interface LeftSidebarProps {
  collapsed?: boolean;
}

export const LeftSidebar: React.FC<LeftSidebarProps> = ({
  collapsed = false,
}) => {
  const [activePopover, setActivePopover] = useState<string | null>(null);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const { pathname } = useLocation();

  const cancelClose = () => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  };

  // Delay closing so the mouse can cross from the icon to the popover.
  // Only close if the same popover is still the open one.
  const scheduleClose = (key: string) => {
    cancelClose();
    closeTimer.current = setTimeout(() => {
      setActivePopover((current) => (current === key ? null : current));
    }, 300);
  };

  useEffect(() => cancelClose, []);

  const handlePopoverVisibleChange = (visible: boolean, key: string) => {
    if (visible) {
      cancelClose();
      setActivePopover(key);
    } else {
      scheduleClose(key);
    }
  };

  const handleNavItemMouseEnter = (key: string) => {
    cancelClose();
    setActivePopover(key);
  };

  const isActive = (key: string) => isNavActive(pathname, key);
  const isParentActive = (option: any) => isNavParentActive(pathname, option);

  const renderSubMenu = (children: any[]) => (
    <div className="left-sidebar-submenu">
      {children.map((child) => {
        const childUrl = getNavUrl(child);

        return (
          <Link
            key={child.key}
            to={childUrl}
            className={`submenu-item ${isActive(child.key) ? 'active' : ''}`}
            onClick={() => {
              // Close popover after a short delay to allow navigation
              setTimeout(() => setActivePopover(null), 100);
            }}
          >
            <IntlMessages id={child.label} />
          </Link>
        );
      })}
    </div>
  );

  const renderNavItem = (option: any) => {
    const hasChildren = option.children && option.children.length > 0;
    const url = getNavUrl(option);

    const iconElement = (
      <div className={`nav-icon ${isParentActive(option) ? 'active' : ''}`}>
        <NavigationIcon iconName={option.leftIcon} size={24} />
      </div>
    );

    if (hasChildren) {
      return (
        <Popover
          key={option.key}
          content={renderSubMenu(option.children)}
          title={<IntlMessages id={option.label} />}
          trigger="hover"
          placement="rightTop"
          open={activePopover === option.key}
          onOpenChange={(visible) =>
            handlePopoverVisibleChange(visible, option.key)
          }
          overlayClassName="left-sidebar-popover"
          mouseEnterDelay={0.1}
          mouseLeaveDelay={0.2}
        >
          <div
            className="nav-item clickable"
            onMouseEnter={() => handleNavItemMouseEnter(option.key)}
          >
            {iconElement}
          </div>
        </Popover>
      );
    }

    return (
      <Tooltip
        key={option.key}
        title={<IntlMessages id={option.label} />}
        placement="right"
        mouseEnterDelay={0.5}
      >
        <Link to={url} className="nav-item">
          {iconElement}
        </Link>
      </Tooltip>
    );
  };

  return (
    <LeftSidebarWrapper
      className={`left-sidebar ${collapsed ? 'collapsed' : ''}`}
    >
      <div className="nav-items">{options.map(renderNavItem)}</div>
    </LeftSidebarWrapper>
  );
};
